import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  AppBar,
  Toolbar,
  Chip,
  CircularProgress,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  Avatar,
  IconButton,
  Tooltip,
  Popper,
  Fade,
  Card as MuiCard,
  Divider,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Badge,
  alpha,
} from '@mui/material';
import {
  Logout as LogoutIcon,
  Person as PersonIcon,
  Palette as PaletteIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  History as HistoryIcon,
  Star as StarIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  UploadFile as UploadIcon,
  Home as HomeIcon,
  Settings as SettingsIcon,
  Verified as VerifiedIcon,
  Receipt as ReceiptIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import api from '../services/api';
import themes from '../theme/themes';

const ProfilePage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user, logout, token } = useAuth();
  const { currentTheme, changeTheme, allThemes } = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [language, setLanguage] = useState(i18n.language);
  const [anchorEl, setAnchorEl] = useState(null);
  const [openPalette, setOpenPalette] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [avatarDialog, setAvatarDialog] = useState(false);
  
  const [profile, setProfile] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
  });
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [stats, setStats] = useState({
    totalRides: 0,
    totalSpent: 0,
    avgRating: 0,
  });

  const fetchProfile = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await api.get(`/users/profile/${user?.id}`);
      setProfile(response.data);
      setFormData({
        name: response.data.name || '',
        phone: response.data.phone || '',
        email: response.data.email || '',
      });
      
      // جلب إحصائيات المستخدم
      const statsResponse = await api.get(`/users/stats/${user?.id}`);
      setStats(statsResponse.data);
      
    } catch (err) {
      console.error('❌ Error fetching profile:', err);
      setError(err.response?.data?.error || t('errorLoadingData'));
    } finally {
      setLoading(false);
    }
  }, [user, t]);

  useEffect(() => {
    if (user) {
      fetchProfile();
    }
  }, [user]);

  const handleLanguageChange = (event, newLang) => {
    if (newLang) {
      setLanguage(newLang);
      i18n.changeLanguage(newLang);
    }
  };

  const handlePaletteClick = (event) => {
    setAnchorEl(event.currentTarget);
    setOpenPalette((prev) => !prev);
  };

  const handleThemeChange = (themeId) => {
    changeTheme(themeId);
    setOpenPalette(false);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAvatarFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUpdateProfile = async () => {
    try {
      setLoading(true);
      await api.put(`/users/profile/${user?.id}`, formData);
      
      // تحديث الصورة الرمزية إذا وجدت
      if (avatarFile) {
        const formDataWithFile = new FormData();
        formDataWithFile.append('avatar', avatarFile);
        await api.post(`/users/avatar/${user?.id}`, formDataWithFile, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      
      setEditMode(false);
      await fetchProfile();
      // تحديث بيانات المستخدم في السياق
      const updatedUser = { ...user, ...formData };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      
    } catch (err) {
      setError(err.response?.data?.error || 'حدث خطأ في تحديث الملف الشخصي');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{
      pb: 8,
      bgcolor: (theme) => theme.palette.background.default,
      minHeight: '100vh',
      transition: 'background 0.5s ease',
    }}>
      <AppBar
        position="static"
        color="transparent"
        elevation={0}
        sx={{
          bgcolor: (theme) => theme.palette.background.paper,
          backdropFilter: 'blur(16px)',
          borderBottom: (theme) => `2px solid ${alpha(theme.palette.primary.main, 0.08)}`,
        }}
      >
        <Toolbar>
          <IconButton onClick={() => navigate(-1)} sx={{ mr: 1 }}>
            <HomeIcon />
          </IconButton>
          <Typography
            variant="h6"
            sx={{
              flexGrow: 1,
              fontWeight: 700,
              background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            👤 {t('profile') || 'الملف الشخصي'}
          </Typography>

          <Tooltip title={t('changeTheme') || 'تغيير الثيم'}>
            <IconButton onClick={handlePaletteClick} sx={{ mx: 0.5 }}>
              <PaletteIcon />
            </IconButton>
          </Tooltip>

          <Popper open={openPalette} anchorEl={anchorEl} placement="bottom-end" transition sx={{ zIndex: 9999 }}>
            {({ TransitionProps }) => (
              <Fade {...TransitionProps} timeout={350}>
                <MuiCard sx={{ p: 2, mt: 1, borderRadius: 3, maxWidth: 280, boxShadow: '0 8px 32px rgba(0,0,0,0.15)' }}>
                  <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>🎨 اختر الثيم المفضل</Typography>
                  <Box display="flex" flexDirection="column" gap={1}>
                    {Object.values(allThemes).map((themeData) => (
                      <Button
                        key={themeData.id}
                        variant={currentTheme === themeData.id ? 'contained' : 'outlined'}
                        size="small"
                        onClick={() => { changeTheme(themeData.id); setOpenPalette(false); }}
                        sx={{ justifyContent: 'flex-start', borderRadius: 2, textTransform: 'none', fontWeight: 500 }}
                      >
                        <Box sx={{ width: 20, height: 20, borderRadius: '50%', mr: 1.5, background: `linear-gradient(135deg, ${themeData.colors.primary}, ${themeData.colors.secondary})`, border: '2px solid rgba(255,255,255,0.3)' }} />
                        <span dir="ltr">{themeData.icon} {themeData.name}</span>
                      </Button>
                    ))}
                  </Box>
                </MuiCard>
              </Fade>
            )}
          </Popper>

          <ToggleButtonGroup value={language} exclusive onChange={handleLanguageChange} size="small" sx={{ mx: 0.5 }}>
            <ToggleButton value="ar">🇸🇦</ToggleButton>
            <ToggleButton value="en">🇬🇧</ToggleButton>
          </ToggleButtonGroup>

          <Button variant="outlined" size="small" onClick={logout} startIcon={<LogoutIcon />} sx={{ borderRadius: 50 }}>
            {t('logout')}
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ mt: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 3 }}>
            {error}
          </Alert>
        )}

        {/* بطاقة البروفايل */}
        <Card sx={{ p: 3, mb: 3, borderRadius: 4, textAlign: 'center' }}>
          <Box position="relative" display="inline-block">
            <Badge
              overlap="circular"
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              badgeContent={
                <IconButton
                  size="small"
                  sx={{ bgcolor: '#FF1493', color: '#fff', '&:hover': { bgcolor: '#C71585' } }}
                  onClick={() => setAvatarDialog(true)}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              }
            >
              <Avatar
                src={avatarPreview || profile?.avatar_url || user?.avatar || ''}
                sx={{
                  width: 120,
                  height: 120,
                  margin: '0 auto',
                  background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  boxShadow: (theme) => `0 8px 32px ${alpha(theme.palette.primary.main, 0.3)}`,
                  fontSize: 48,
                }}
              >
                {!avatarPreview && !profile?.avatar_url && !user?.avatar && (
                  <PersonIcon sx={{ fontSize: 60 }} />
                )}
              </Avatar>
            </Badge>
          </Box>

          <Box mt={2}>
            {editMode ? (
              <TextField
                fullWidth
                label={t('name') || 'الاسم'}
                name="name"
                value={formData.name}
                onChange={handleChange}
                sx={{ mb: 1 }}
              />
            ) : (
              <Typography variant="h4" fontWeight={700}>
                {profile?.name || user?.name}
              </Typography>
            )}
            <Chip
              label={profile?.role === 'driver' ? (t('captain') || 'سائقة') : (t('passenger') || 'راكبة')}
              size="small"
              sx={{
                mt: 1,
                background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                color: '#FFFFFF',
                fontWeight: 600,
              }}
            />
            {profile?.is_verified && (
              <Chip
                icon={<VerifiedIcon />}
                label={t('verified') || 'موثق'}
                size="small"
                color="success"
                sx={{ mt: 1, ml: 1 }}
              />
            )}
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box display="flex" justifyContent="space-around" flexWrap="wrap" gap={2}>
            <Box textAlign="center">
              <Typography variant="caption" color="text.secondary">
                {t('balance') || 'الرصيد'}
              </Typography>
              <Typography variant="h5" fontWeight={700} color="primary.main">
                {profile?.balance || 0} {t('riyal') || 'ريال'}
              </Typography>
            </Box>
            <Box textAlign="center">
              <Typography variant="caption" color="text.secondary">
                {t('totalRides') || 'إجمالي الرحلات'}
              </Typography>
              <Typography variant="h5" fontWeight={700}>
                {stats.totalRides || 0}
              </Typography>
            </Box>
            <Box textAlign="center">
              <Typography variant="caption" color="text.secondary">
                {t('rating') || 'التقييم'}
              </Typography>
              <Typography variant="h5" fontWeight={700} color="#FFD700">
                {stats.avgRating || 5} ⭐
              </Typography>
            </Box>
            <Box textAlign="center">
              <Typography variant="caption" color="text.secondary">
                {t('totalSpent') || 'إجمالي المصروف'}
              </Typography>
              <Typography variant="h5" fontWeight={700} color="#FF9800">
                {stats.totalSpent || 0} {t('riyal') || 'ريال'}
              </Typography>
            </Box>
          </Box>
        </Card>

        {/* معلومات الاتصال */}
        <Card sx={{ p: 3, mb: 3, borderRadius: 4 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" fontWeight={600}>
              📞 {t('contactInfo') || 'معلومات الاتصال'}
            </Typography>
            <Button
              variant={editMode ? 'outlined' : 'contained'}
              size="small"
              startIcon={editMode ? <CloseIcon /> : <EditIcon />}
              onClick={() => setEditMode(!editMode)}
              sx={{ borderRadius: 50 }}
            >
              {editMode ? (t('cancel') || 'إلغاء') : (t('edit') || 'تعديل')}
            </Button>
          </Box>

          {editMode ? (
            <>
              <TextField
                fullWidth
                label={t('email') || 'البريد الإلكتروني'}
                name="email"
                value={formData.email}
                onChange={handleChange}
                disabled
                sx={{ mb: 2 }}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><EmailIcon /></InputAdornment>,
                }}
              />
              <TextField
                fullWidth
                label={t('phone') || 'رقم الجوال'}
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                sx={{ mb: 2 }}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><PhoneIcon /></InputAdornment>,
                }}
              />
              <Button
                fullWidth
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleUpdateProfile}
                disabled={loading}
                sx={{ borderRadius: 50, mt: 1 }}
              >
                {loading ? <CircularProgress size={24} /> : (t('save') || 'حفظ التغييرات')}
              </Button>
            </>
          ) : (
            <>
              <Box display="flex" alignItems="center" gap={2} mb={1}>
                <EmailIcon color="action" />
                <Typography variant="body1">{profile?.email || user?.email}</Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={2}>
                <PhoneIcon color="action" />
                <Typography variant="body1">{profile?.phone || user?.phone || t('notProvided') || 'غير مدخل'}</Typography>
              </Box>
            </>
          )}
        </Card>

        {/* معلومات إضافية للكابتن */}
        {profile?.role === 'driver' && (
          <Card sx={{ p: 3, mb: 3, borderRadius: 4 }}>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
              🚗 {t('captainInfo') || 'معلومات الكابتن'}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  {t('carModel') || 'موديل السيارة'}
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  {profile?.car_model || t('notSet') || 'غير محدد'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  {t('carPlate') || 'رقم اللوحة'}
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  {profile?.car_plate_ar || profile?.car_plate_en || t('notSet') || 'غير محدد'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  {t('carColor') || 'لون السيارة'}
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  {profile?.car_color || t('notSet') || 'غير محدد'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  {t('experience') || 'سنوات الخبرة'}
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  {profile?.experience_years || 0} {t('years') || 'سنوات'}
                </Typography>
              </Grid>
            </Grid>
          </Card>
        )}

        {/* أزرار إضافية */}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<HistoryIcon />}
              onClick={() => navigate('/')}
              sx={{ borderRadius: 50, py: 1.5 }}
            >
              {t('myRides') || 'رحلاتي'}
            </Button>
          </Grid>
          {profile?.role === 'driver' && (
            <Grid item xs={12} sm={6}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<SettingsIcon />}
                onClick={() => navigate('/captain')}
                sx={{ borderRadius: 50, py: 1.5 }}
              >
                {t('captainDashboard') || 'لوحة الكابتن'}
              </Button>
            </Grid>
          )}
        </Grid>
      </Container>

      {/* حوار تغيير الصورة الرمزية */}
      <Dialog open={avatarDialog} onClose={() => setAvatarDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">{t('changeAvatar') || 'تغيير الصورة الشخصية'}</Typography>
            <IconButton onClick={() => setAvatarDialog(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box textAlign="center" py={2}>
            <Avatar
              src={avatarPreview || profile?.avatar_url || user?.avatar || ''}
              sx={{
                width: 150,
                height: 150,
                margin: '0 auto',
                background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              }}
            />
            <Button
              variant="contained"
              component="label"
              fullWidth
              sx={{ mt: 3, borderRadius: 50, py: 1.5 }}
              startIcon={<UploadIcon />}
            >
              {t('uploadImage') || 'رفع صورة'}
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={handleAvatarChange}
              />
            </Button>
            {avatarFile && (
              <Button
                variant="outlined"
                color="success"
                fullWidth
                sx={{ mt: 2, borderRadius: 50 }}
                onClick={handleUpdateProfile}
                startIcon={<SaveIcon />}
              >
                {t('saveAvatar') || 'حفظ الصورة'}
              </Button>
            )}
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ProfilePage;
